/* eslint-disable @typescript-eslint/no-unused-vars */
import { Filter, Grid3X3, List, Loader2, Plus, Search } from 'lucide-react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dataProvider from '../../authentication/dataProvider';
import { generateUserKey } from '../../services/sessionKey';
import { services } from '../../services';
import { actions } from '../../actions';
import { Asset, AssetFormData, AssetStatus } from '../../types/asset';
import {
  CategoryRecord,
  extractAssetCategory,
  getCategoryDisplayName,
  getCategoryId,
} from '../../utils/categoryUtils';
import { NFTRecord } from '../../types/nft/nfts';
import { AddAssetModal } from '../assets/AddAssetModal';
import { AssetCard } from '../assets/AssetCard';
import { AssetDetailModal } from '../assets/AssetDetailModal';
import { EditAssetModal } from '../assets/EditAssetModal';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { useToast } from '../ui/use-toast';

const allStatuses: AssetStatus[] = [
  'DRAFT',
  'UNDER_REVIEW',
  'ACTIVE',
  'DISTRIBUTING',
  'MATURE',
  'EXITED',
  'PENDING',
  'APPROVED',
  'REJECTED',
  'active',
  'inactive',
];

/**
 * NFT ASSETS MANAGEMENT COMPONENT
 *
 * PERFORMANCE OPTIMIZATION:
 * - Implements server-side filtering and pagination
 * - Passes search, category, and status filters to API
 * - Eliminates client-side filtering of large datasets
 * - Improves performance for large asset collections
 */

/**
 * Safely calculate tokens issued from total invested amount and asset price
 * CRITICAL: Assumes both values are in the SAME currency units
 *
 * This function includes comprehensive validation to prevent:
 * - Division by zero
 * - Invalid number conversions
 * - Negative values
 * - Non-finite results
 * - Currency unit mismatches
 *
 * @param totalInvestedAmount Total amount invested (should be in same currency as assetPrice)
 * @param assetPrice Price per token (should be in same currency as totalInvestedAmount)
 * @param assetId Asset identifier for logging/debugging purposes
 * @returns Number of tokens that should be issued (always >= 0, floored to whole number)
 */
function calculateTokensIssuedSafely(
  totalInvestedAmount: number | string,
  assetPrice: number | string,
  assetId: string
): number {
  // Input validation and conversion
  const assetPriceNum = Number(assetPrice);
  const totalInvestedNum = Number(totalInvestedAmount);

  // Validate finite numbers
  if (!Number.isFinite(assetPriceNum) || !Number.isFinite(totalInvestedNum)) {
    console.warn(
      `Invalid numeric inputs for asset ${assetId}: price=${assetPrice}, invested=${totalInvestedAmount}`
    );
    return 0;
  }

  // Business logic validation
  if (assetPriceNum <= 0) {
    console.warn(`Invalid asset price for asset ${assetId}: ${assetPriceNum} (must be positive)`);
    return 0;
  }

  if (totalInvestedNum < 0) {
    console.warn(
      `Invalid total invested amount for asset ${assetId}: ${totalInvestedNum} (cannot be negative)`
    );
    return 0;
  }

  // Zero investment check
  if (totalInvestedNum === 0) {
    return 0;
  }

  // Calculate tokens
  const calculatedTokens = totalInvestedNum / assetPriceNum;

  // Result validation
  if (!Number.isFinite(calculatedTokens) || calculatedTokens < 0) {
    console.error(`Invalid token calculation result for asset ${assetId}: ${calculatedTokens}`);
    return 0;
  }

  // Check for significant remainders (potential currency inconsistencies)
  const remainder = totalInvestedNum % assetPriceNum;
  const remainderPercentage = remainder / assetPriceNum;

  // Log warnings for significant remainders (>1% of token price)
  if (remainder > 0 && remainderPercentage > 0.01) {
    console.warn(
      `Potential currency inconsistency for asset ${assetId}: ` +
        `remainder=${remainder.toFixed(4)} (${(remainderPercentage * 100).toFixed(2)}% of token price)`
    );
  }

  // Return whole tokens only (floor to prevent fractional tokens)
  const wholeTokens = Math.floor(calculatedTokens);

  // Log calculation summary for debugging
  if (process.env.NODE_ENV === 'development') {
    console.debug(
      `Token calculation for asset ${assetId}: ` +
        `${totalInvestedNum} / ${assetPriceNum} = ${calculatedTokens.toFixed(4)} → ${wholeTokens} tokens`
    );
  }

  return wholeTokens;
}

// Helper function to map NFTRecord to Asset
// Using type assertion since API response has more fields than NFTRecord type
const mapNFTRecordToAsset = (nft: NFTRecord & Record<string, unknown>): Asset => {
  // Type assertion to access additional fields that may exist in API response
  const nftData = nft as NFTRecord & {
    category?: string;
    location?: string;
    valuation?: number;
    tokenSymbol?: string;
    tokensIssued?: number;
    yieldRate?: number;
    annualYield?: number;
    description?: string;
    investorCount?: number;
    totalInvestorsCount?: number; // Backend returns this
    totalSupplyAmount?: number; // Backend returns this
    totalInvestedAmount?: number; // Backend returns this
    createdAt?: string;
    updatedAt?: string;
    AssetDetials?: Asset['AssetDetials'];
    symbol?: string; // Backend has 'symbol', not 'tokenSymbol'
    socialYtUrl?: string; // Backend has 'socialYtUrl', not 'youtubeUrl'
    youtubeUrl?: string; // May exist in some responses
    assetOwner?: string | { _id?: string; walletAddress?: string }; // May be populated
    ownerAddress?: string;
  };

  // Determine category from nft data
  const { categoryId, categoryName } = extractAssetCategory(nft.category);

  // Map status
  let status: AssetStatus = 'DRAFT';
  if (nft.status) {
    const stat = String(nft.status).toUpperCase();
    if (allStatuses.includes(stat as AssetStatus)) {
      status = stat as AssetStatus;
    } else if (nft.status === 'active') {
      status = 'ACTIVE';
    } else if (nft.status === 'inactive') {
      status = 'inactive';
    }
  }

  // Create location from city and countryCode if location doesn't exist
  let location = nftData.location || '';
  if (!location && (nft.city || nft.countryCode)) {
    const parts = [nft.city, nft.countryCode].filter(Boolean);
    location = parts.join(', ');
  }

  // Map tokenSymbol from symbol
  const tokenSymbol = nftData.tokenSymbol || nftData.symbol || '';

  // Map youtubeUrl from socialYtUrl
  const youtubeUrl =
    nftData.youtubeUrl ||
    nftData.socialYtUrl ||
    ((nft as Record<string, unknown>).socialYtUrl as string | undefined);

  // Get ownerAddress from assetOwner if populated
  let ownerAddress = nftData.ownerAddress;
  if (!ownerAddress && nftData.assetOwner) {
    if (typeof nftData.assetOwner === 'object' && nftData.assetOwner.walletAddress) {
      ownerAddress = nftData.assetOwner.walletAddress;
    }
  }

  // Calculate valuation: prefer totalSupplyAmount from backend, then computed value
  const valuation =
    nftData.valuation ||
    nftData.totalSupplyAmount ||
    (nft.assetPrice ? Number(nft.assetPrice) * Number(nft.maxSupply || 0) : 0);

  // Map investorCount from totalInvestorsCount (backend field name)
  const investorCount = nftData.investorCount || nftData.totalInvestorsCount || 0;

  // Calculate tokensIssued from totalInvestedAmount if available
  // CRITICAL: This calculation assumes totalInvestedAmount and assetPrice are in the SAME currency units
  // totalInvestedAmount should be: (tokens_purchased * price_per_token) in the same currency as assetPrice
  // Example: If assetPrice is $10 per token and someone invested $1000, tokensIssued should be 100
  let tokensIssued = nftData.tokensIssued || 0;
  if (!tokensIssued && nftData.totalInvestedAmount && nft.assetPrice) {
    tokensIssued = calculateTokensIssuedSafely(
      nftData.totalInvestedAmount,
      nft.assetPrice,
      nft.id || nft._id || 'unknown'
    );
  }

  return {
    id: nft.id || nft._id || '',
    _id: nft._id,
    name: nft.assetName || '',
    assetName: nft.assetName,
    categoryId,
    categoryName,
    location,
    valuation,
    tokenSymbol,
    totalTokens: nft.maxSupply || 0,
    maxSupply: nft.maxSupply,
    tokensIssued,
    yieldRate: nftData.yieldRate || nftData.annualYield || 0,
    annualYield: nftData.annualYield,
    status,
    description: nftData.description || '',
    assetAddress: nft.assetAddress,
    assetPrice: nft.assetPrice,
    image: nft.image,
    investorCount,
    createdAt: nftData.createdAt,
    updatedAt: nftData.updatedAt,
    totalRecords: nft.totalRecords,
    allowPayRent: nft.allowPayRent,
    AssetDetials: nftData.AssetDetials,
    // Additional fields
    countryCode: (nft.countryCode as string) || undefined,
    city: (nft.city as string) || undefined,
    locationUrl: (nft.locationUrl as string) || undefined,
    youtubeUrl: youtubeUrl || undefined,
    socialYtUrl:
      nftData.socialYtUrl || ((nft as Record<string, unknown>).socialYtUrl as string | undefined),
    startTime: (nft.startTime as string) || undefined,
    endTime: (nft.endTime as string) || undefined,
    saleEndDate: (nft.saleEndDate as string) || undefined,
    buyTokenAddress: (nft.buyTokenAddress as string) || undefined,
    minPurchaseToken: (nft.minPurchaseToken as string) || undefined,
    maxPurchaseToken: (nft.maxPurchaseToken as string) || undefined,
    platformFeePercentage: (nft.platformFeePercentage as string) || undefined,
    platformFeeRecipient: (nft.platformFeeRecipient as string) || undefined,
    assetOwner: (nft.assetOwner as string) || undefined,
    ownerAddress: ownerAddress || undefined,
    minHoldingDuration: (nft.minHoldingDuration as number) || undefined,
    isListedForResell: (nft.isListedForResell as boolean) || undefined,
    txHash: (nft.txHash as string) || undefined,
    isFeatured: Boolean(nft.isFeatured),
  };
};

const NFTList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast: showToast } = useToast();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [categoriesList, setCategoriesList] = useState<CategoryRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<AssetStatus[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [detailAsset, setDetailAsset] = useState<Asset | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailDefaultTab, setDetailDefaultTab] = useState<'details' | 'performance'>('details');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [assetName, setAssetName] = useState<string | null>(null);
  const [showPopUp, setShowPopup] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [web3Initialized, setWeb3Initialized] = useState<boolean>(false);
  // API may ignore the `limit` param and return a fixed page size (e.g. 20).
  // We track that real page size and cache API pages so frontend pages can be
  // served by slicing the right segment without extra network calls.
  const apiLimitRef = useRef<number>(0);
  const apiCacheRef = useRef<Record<number, Asset[]>>({});

  useEffect(() => {
    actions
      .getAssetCategories()
      .then((response) => {
        if (response?.data) {
          setCategoriesList(response.data);
        }
      })
      .catch(() => {
        // Categories are optional for list rendering
      });
  }, []);

  useEffect(() => {
    if (searchParams.get('openCreate') === '1') {
      setIsAddModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openCreate');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Clear the API cache whenever the dataset changes so stale pages aren't served
  useEffect(() => {
    apiCacheRef.current = {};
    apiLimitRef.current = 0;
  }, [search, categoryFilter, statusFilter, perPage]);

  const fetchNFTs = useCallback(async () => {
    try {
      setIsLoading(true);

      const filter: Record<string, string | string[]> = {};
      if (search.trim()) filter.search = search.trim();
      if (categoryFilter.length > 0) filter.category = categoryFilter;
      if (statusFilter.length > 0) filter.status = statusFilter;

      // Map frontend page → API page using the known API page size.
      // If the API ignores `limit` and returns a fixed page size, multiple
      // frontend pages may come from the same API page (just different slices).
      const knownApiLimit = apiLimitRef.current;
      const apiPage = knownApiLimit > 0
        ? Math.ceil(page * perPage / knownApiLimit)
        : page; // no info yet — assume 1:1
      const localOffset = knownApiLimit > 0
        ? ((page - 1) * perPage) % knownApiLimit
        : 0;

      // Serve from cache when possible (avoids duplicate API calls)
      if (knownApiLimit > 0 && apiCacheRef.current[apiPage] !== undefined) {
        const cached = apiCacheRef.current[apiPage];
        setAssets(cached.slice(localOffset, localOffset + perPage));
        setIsLoading(false);
        return;
      }

      const response = await dataProvider.getList('assets', {
        pagination: { page: apiPage, perPage },
        sort: { field: 'id', order: 'DESC' },
        filter,
      });

      const categoryNameById = new Map(
        categoriesList.map((category) => [getCategoryId(category), getCategoryDisplayName(category)])
      );

      const mappedAssets = response.data.map((nft) => {
        const asset = mapNFTRecordToAsset(nft);
        if (asset.categoryId && asset.categoryName === 'Uncategorized') {
          asset.categoryName = categoryNameById.get(asset.categoryId) || asset.categoryName;
        }
        return asset;
      });

      // Learn the API's real page size from the response; fall back to items returned
      type AssetListResponse = { total: number; data: unknown[]; pageCount?: number; apiLimit?: number };
      const responseApiLimit = (response as AssetListResponse).apiLimit || mappedAssets.length;
      if (responseApiLimit > 0) apiLimitRef.current = responseApiLimit;
      apiCacheRef.current[apiPage] = mappedAssets;

      // Re-derive offset now that we know the actual API limit
      const finalApiLimit = apiLimitRef.current || mappedAssets.length;
      const finalOffset = ((page - 1) * perPage) % finalApiLimit;
      const pageAssets = mappedAssets.slice(finalOffset, finalOffset + perPage);

      const apiTotal = response.total || mappedAssets.length;
      // Always use frontend perPage for page count so "Show: 10" is meaningful
      const newPageCount = Math.ceil(apiTotal / perPage) || 1;

      setTotal(apiTotal);
      setTotalPages(newPageCount);

      // Snap to last valid page if current page is beyond actual data
      if (pageAssets.length === 0 && page > 1 && page > newPageCount) {
        setPage(newPageCount);
        return;
      }
      setAssets(pageAssets);
      setIsLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching NFTs:', error);
      setIsLoading(false);
    }
  }, [page, perPage, search, categoryFilter, statusFilter, categoriesList]);

  useEffect(() => {
    fetchNFTs();
  }, [fetchNFTs]);

  useEffect(() => {
    // Initialize Web3 connection
    const initWeb3 = async () => {
      try {
        const { getWeb3 } = await import('../../web3');
        await getWeb3();
        setWeb3Initialized(true);
      } catch (error) {
        setWeb3Initialized(false);
      }
    };

    initWeb3();
  }, []);

  // Note: Filtering is now handled server-side in dataProvider.getList
  // This improves performance by reducing data transfer and client-side processing

  const toggleCategory = (categoryId: string) => {
    setPage(1);
    if (categoryFilter.includes(categoryId)) {
      setCategoryFilter(categoryFilter.filter((c) => c !== categoryId));
    } else {
      setCategoryFilter([...categoryFilter, categoryId]);
    }
  };

  const toggleStatus = (status: AssetStatus) => {
    setPage(1);
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter((s) => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const result = await generateUserKey();
      const userKey = result.userKey;

      await fetch(`${process.env.REACT_APP_API_URL}/asset/delete/${id}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json',
          sessionKey: userKey,
        },
      });

      setAssets(assets.filter((item) => (item.id || item._id) !== id));
      setShowPopup(false);
      showToast({ description: 'Asset deleted', variant: 'default' });
      setDeleteId(null);
      // Refresh the list
      fetchNFTs();
    } catch (error) {
      showToast({ description: 'Error deleting asset', variant: 'destructive' });
    }
  };

  const handleAddAsset = (formData: AssetFormData) => {
    // The AddAssetModal handles the API call, so we just refresh the list
    fetchNFTs();
  };

  const handleEditAsset = (asset: Asset) => {
    setEditingAsset(asset);
    setIsEditModalOpen(true);
  };

  const handleFeaturedToggle = async (asset: Asset, isFeatured: boolean) => {
    const assetId = asset.id || asset._id;
    if (!assetId) return;

    try {
      const response = await services.patch(`asset/${assetId}/featured`, { isFeatured });
      if (response?.status === 200) {
        showToast({
          description: isFeatured
            ? `"${asset.name || asset.assetName}" is now the featured investment`
            : 'Asset removed from featured',
          variant: 'default',
        });
        fetchNFTs();
      } else {
        throw new Error('Failed to update featured asset');
      }
    } catch {
      showToast({
        description: 'Failed to update featured asset',
        variant: 'destructive',
      });
    }
  };

  const handleSaveAsset = (updatedAsset: Asset) => {
    // The EditAssetModal handles the API call, so we just refresh the list
    fetchNFTs();
    setIsEditModalOpen(false);
  };

  const handleDeleteAsset = (assetId: string) => {
    const asset = assets.find((a) => (a.id || a._id) === assetId);
    if (asset) {
      setDeleteId(assetId);
      setAssetName(asset.name || asset.assetName || '');
      setShowPopup(true);
    }
  };

  const handleViewDetails = (asset: Asset) => {
    setDetailAsset(asset);
    setDetailDefaultTab('details');
    setIsDetailModalOpen(true);
  };

  const handleViewPerformance = (asset: Asset) => {
    setDetailAsset(asset);
    setDetailDefaultTab('performance');
    setIsDetailModalOpen(true);
  };

  // Summary stats
  const totalValuation = assets.reduce((sum, a) => sum + (a.valuation || 0), 0);
  const activeAssets = assets.filter((a) =>
    ['ACTIVE', 'DISTRIBUTING', 'active'].includes(a.status)
  ).length;

  const pageCount = totalPages || Math.ceil(total / perPage);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (pageCount <= 7) {
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      const start = Math.max(2, page - 1);
      const end = Math.min(pageCount - 1, page + 1);
      pages.push(1);
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < pageCount - 1) pages.push('...');
      pages.push(pageCount);
    }
    return pages;
  };

  if (isLoading && assets.length === 0) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <Fragment>
      <div className='space-y-6'>
        {/* Page Header */}
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl font-bold text-foreground'>Assets</h1>
            <p className='text-muted-foreground'>
              Manage tokenized assets across {total}{' '}
              {total === 1 ? 'property' : 'properties'}
              {totalValuation > 0 && (
                <>
                  {' • '}
                  <span className='text-primary font-medium'>
                    ${(totalValuation / 1000000).toFixed(1)}M
                  </span>
                  {' total AUM'}
                </>
              )}
              {activeAssets > 0 && (
                <>
                  {' • '}
                  <span className='text-success font-medium'>{activeAssets} active</span>
                </>
              )}
            </p>
          </div>
          <div className='flex gap-2 flex-wrap'>
            {/* <Button variant='outline' onClick={() => navigate('/assets/create')} className='gap-2'>
              <Plus className='w-4 h-4' />
              <span className='hidden sm:inline'>Create Asset</span>
              <span className='sm:hidden'>Create</span>
            </Button> */}
            <Button className='gap-2' onClick={() => setIsAddModalOpen(true)}>
              <Plus className='w-4 h-4' />
              <span className='hidden sm:inline'>Add Asset</span>
              <span className='sm:hidden'>Add</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
          <div className='flex flex-1 gap-3 items-center'>
            {/* Search */}
            <div className='relative flex-1 max-w-sm'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
              <Input
                placeholder='Search assets...'
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className='pl-10'
              />
            </div>

            {/* Category Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' className='gap-2'>
                  <Filter className='w-4 h-4' />
                  <span className='hidden sm:inline'>Category</span>
                  {categoryFilter.length > 0 && (
                    <span className='ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary text-primary-foreground'>
                      {categoryFilter.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start' className='w-48'>
                <DropdownMenuLabel>Asset Category</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {categoriesList.map((category) => {
                  const categoryId = getCategoryId(category);
                  return (
                    <DropdownMenuCheckboxItem
                      key={categoryId}
                      checked={categoryFilter.includes(categoryId)}
                      onCheckedChange={() => toggleCategory(categoryId)}
                    >
                      {getCategoryDisplayName(category)}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' className='gap-2'>
                  <span className='hidden sm:inline'>Status</span>
                  {statusFilter.length > 0 && (
                    <span className='ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary text-primary-foreground'>
                      {statusFilter.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start' className='w-48'>
                <DropdownMenuLabel>Asset Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {allStatuses.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                  >
                    {status.replace(/_/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* View Toggle */}
          <div className='flex items-center gap-1 bg-muted rounded-lg p-1'>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size='icon'
              onClick={() => setViewMode('grid')}
              className='h-8 w-8'
            >
              <Grid3X3 className='w-4 h-4' />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size='icon'
              onClick={() => setViewMode('list')}
              className='h-8 w-8'
            >
              <List className='w-4 h-4' />
            </Button>
          </div>
        </div>

        {/* Asset Grid */}
        {viewMode === 'grid' ? (
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
            {assets.map((asset) => (
              <AssetCard
                key={asset.id || asset._id}
                asset={asset}
                onEdit={handleEditAsset}
                onDelete={handleDeleteAsset}
                onViewDetails={handleViewDetails}
                onViewPerformance={handleViewPerformance}
                onFeaturedToggle={handleFeaturedToggle}
              />
            ))}
          </div>
        ) : (
          <div className='space-y-4'>
            {assets.map((asset) => (
              <AssetCard
                key={asset.id || asset._id}
                asset={asset}
                onEdit={handleEditAsset}
                onDelete={handleDeleteAsset}
                onViewDetails={handleViewDetails}
                onViewPerformance={handleViewPerformance}
                onFeaturedToggle={handleFeaturedToggle}
              />
            ))}
          </div>
        )}

        {assets.length === 0 && !isLoading && (
          <div className='text-center py-12 text-muted-foreground'>
            <p className='mb-4'>No assets found matching your criteria.</p>
            <Button onClick={() => setIsAddModalOpen(true)}>Add First Asset</Button>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className='flex flex-col lg:flex-row items-center justify-between gap-4 py-4 px-4 sm:py-6 sm:px-6 border-t'>
            {/* Results Info */}
            <div className='flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground'>
              <span>Showing</span>
              <span className='font-semibold text-foreground px-1.5 py-0.5 rounded bg-muted'>
                {Math.min((page - 1) * perPage + 1, total)}
              </span>
              <span>to</span>
              <span className='font-semibold text-foreground px-1.5 py-0.5 rounded bg-muted'>
                {Math.min(page * perPage, total)}
              </span>
              <span>of</span>
              <span className='font-semibold text-foreground px-1.5 py-0.5 rounded bg-muted'>
                {total}
              </span>
              <span>assets</span>
            </div>

            {/* Pagination Controls */}
            <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
              {/* Items per page selector */}
              <div className='flex items-center gap-2 w-full sm:w-auto justify-end'>
                <label className='text-xs sm:text-sm font-medium text-muted-foreground'>Show:</label>
                <select
                  className='h-8 w-16 rounded-md border px-2 text-xs sm:text-sm bg-background text-foreground'
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 30, 50].map((value) => (
                    <option key={value} value={value} className='bg-card text-foreground'>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              {/* Page navigation with numbered buttons */}
              <div className='flex items-center gap-1.5 w-full sm:w-auto justify-center'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className='h-8 px-2.5 text-xs sm:text-sm'
                >
                  Previous
                </Button>

                <div className='hidden sm:flex items-center gap-1'>
                  {getPageNumbers().map((p, idx) =>
                    p === '...' ? (
                      <span key={`dots-${idx}`} className='px-1.5 text-xs text-muted-foreground'>
                        ...
                      </span>
                    ) : (
                      <Button
                        key={p}
                        variant={page === p ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setPage(Number(p))}
                        className={`h-8 w-8 p-0 text-xs sm:text-sm font-medium ${
                          page === p ? 'bg-primary text-primary-foreground font-semibold shadow-sm' : ''
                        }`}
                      >
                        {p}
                      </Button>
                    )
                  )}
                </div>

                <div className='sm:hidden flex items-center px-2 py-1 text-xs font-medium text-muted-foreground'>
                  {page} / {pageCount || 1}
                </div>

                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage((p) => (p < pageCount ? p + 1 : p))}
                  disabled={page >= pageCount}
                  className='h-8 px-2.5 text-xs sm:text-sm'
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {showPopUp && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
          <div className='p-4 sm:p-6 rounded-lg shadow-lg max-w-sm w-full mx-4 bg-background border'>
            <h3 className='text-lg font-semibold mb-4'>Delete Asset - {assetName}</h3>
            <p className='mb-6 text-sm sm:text-base text-muted-foreground'>
              Are you sure you want to delete this asset?
            </p>
            <div className='flex flex-col sm:flex-row justify-end gap-3'>
              <Button
                variant='outline'
                onClick={() => {
                  setShowPopup(false);
                  setDeleteId(null);
                }}
                className='w-full sm:w-auto'
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => deleteId && handleDelete(deleteId)}
                className='w-full sm:w-auto'
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddAssetModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onAdd={handleAddAsset}
      />

      <EditAssetModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        asset={editingAsset}
        onSave={handleSaveAsset}
      />

      <AssetDetailModal
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        asset={detailAsset}
        defaultTab={detailDefaultTab}
      />
    </Fragment>
  );
};

export default NFTList;
